class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners () {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets (disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage (searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    if (!sections.length) return;

    FacetFiltersForm.setLoadingState(true);

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, searchParams)
        : FacetFiltersForm.renderSectionFromFetch(url, event, searchParams);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch (url, event, searchParams = '') {
    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error(`Failed to render section (${response.status})`);
        return response.text();
      })
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger();
        FacetFiltersForm.dispatchProductGridUpdated(searchParams, 'facets-fetch');
      })
      .catch((error) => {
        console.error('Facet section fetch failed', error);
      })
      .finally(() => {
        FacetFiltersForm.setLoadingState(false);
      });
  }

  static renderSectionFromCache (filterDataUrl, event, searchParams = '') {
    try {
      const cacheEntry = FacetFiltersForm.filterData.find(filterDataUrl);
      if (!cacheEntry) return;

      const html = cacheEntry.html;
      FacetFiltersForm.renderFilters(html, event);
      FacetFiltersForm.renderProductGridContainer(html);
      FacetFiltersForm.renderProductCount(html);
      if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger();
      FacetFiltersForm.dispatchProductGridUpdated(searchParams, 'facets-cache');
    } catch (error) {
      console.error('Facet section cache render failed', error);
    } finally {
      FacetFiltersForm.setLoadingState(false);
    }
  }

  static renderProductGridContainer (html) {
    const container = document.getElementById('ProductGridContainer');
    if (!container) return;

    const nextContainer = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductGridContainer');
    if (!nextContainer) return;

    container.innerHTML = nextContainer.innerHTML;

    FacetFiltersForm.normalizeProductGridVisibility(container);

    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(container);
  }

  static renderProductCount (html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const sourceCount = parsed.getElementById('ProductCount');
    if (!sourceCount) return;

    const count = sourceCount.innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    if (!container) return;

    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters (html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets (html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements (html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      const sourceElement = html.querySelector(selector);
      const targetElement = document.querySelector(selector);
      if (!sourceElement || !targetElement) return;
      targetElement.innerHTML = sourceElement.innerHTML;
    });

    const mobileFacetsForm = document.getElementById('FacetFiltersFormMobile');
    if (!mobileFacetsForm) return;

    const mobileDrawer = mobileFacetsForm.closest('menu-drawer');
    if (mobileDrawer) mobileDrawer.bindEvents();
  }

  static renderCounts (source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts (source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash (searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections () {
    const productGrid = document.getElementById('product-grid');
    if (!productGrid || !productGrid.dataset.id) return [];

    return [
      {
        section: productGrid.dataset.id,
      },
    ];
  }

  static setLoadingState (isLoading) {
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );

    loadingSpinners.forEach((spinner) => spinner.classList.toggle('hidden', !isLoading));

    const productGridContainer = document.getElementById('ProductGridContainer');
    const collection = productGridContainer ? productGridContainer.querySelector('.collection') : null;
    if (collection) collection.classList.toggle('loading', isLoading);

    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    if (countContainer) countContainer.classList.toggle('loading', isLoading);
    if (countContainerDesktop) countContainerDesktop.classList.toggle('loading', isLoading);
  }

  static dispatchProductGridUpdated (searchParams = '', source = 'facets') {
    document.dispatchEvent(
      new CustomEvent('collection:product-grid:updated', {
        detail: {
          searchParams,
          source,
        },
      })
    );
  }

  static normalizeProductGridVisibility (container = document.getElementById('ProductGridContainer')) {
    if (!container) return;

    if (container.classList && container.classList.contains('scroll-trigger')) {
      container.classList.add('scroll-trigger--cancel');
      container.classList.remove('scroll-trigger--offscreen');
    }

    container.querySelectorAll('.scroll-trigger').forEach((element) => {
      element.classList.add('scroll-trigger--cancel');
      element.classList.remove('scroll-trigger--offscreen');
    });

  }

  createSearchParams (form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm (searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler (event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick (event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();
FacetFiltersForm.normalizeProductGridVisibility();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange (event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown (event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues () {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues (input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter (event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);

/* Keep in-stock products first across paginated collection pages.
   Runs on initial load and after facet-driven section updates. */
(function () {
  if (window.__collectionAvailabilityReorderInit) return;
  window.__collectionAvailabilityReorderInit = true;

  const state = {
    runId: 0,
    queued: false,
    abortController: null,
    pageDataCache: new Map(),
    pageDataPromiseCache: new Map(),
  };

  const MAX_CACHED_PAGES = 120;
  const MAX_CONCURRENT_FETCHES = 4;

  function hasBlockedQueryParams () {
    const qs = new URLSearchParams(window.location.search);
    let shouldSkip = false;

    qs.forEach((_, key) => {
      if (key.indexOf('filter.') === 0 || key === 'sort_by') {
        shouldSkip = true;
      }
    });

    return shouldSkip;
  }

  function isReorderForceDisabled () {
    const qs = new URLSearchParams(window.location.search);
    if (qs.get('disable_reorder') === '1') return true;
    if (window.__disableCollectionReorder === true) return true;
    return false;
  }

  function arraysEqual (left, right) {
    if (left.length !== right.length) return false;

    for (let i = 0; i < left.length; i += 1) {
      if (left[i] !== right[i]) return false;
    }

    return true;
  }

  function parseSectionId (grid) {
    let sectionId = grid.dataset.id || null;
    const wrapper = grid.closest('.shopify-section');

    if (wrapper && wrapper.id.indexOf('shopify-section-') === 0) {
      sectionId = wrapper.id.replace('shopify-section-', '');
    }

    return sectionId;
  }

  function getGridContext () {
    const grid = document.getElementById('product-grid');
    if (!grid || !grid.dataset.collectionHandle) return null;

    return {
      grid,
      perPage: parseInt(grid.dataset.perPage, 10) || 20,
      currentPage: parseInt(grid.dataset.currentPage, 10) || 1,
      totalPages: parseInt(grid.dataset.totalPages, 10) || 1,
      sectionId: parseSectionId(grid),
    };
  }

  function touchPageCache (url, data) {
    if (state.pageDataCache.has(url)) {
      state.pageDataCache.delete(url);
    }

    state.pageDataCache.set(url, data);

    while (state.pageDataCache.size > MAX_CACHED_PAGES) {
      const oldestKey = state.pageDataCache.keys().next().value;
      state.pageDataCache.delete(oldestKey);
    }
  }

  function buildSectionPageUrl (pageNum, sectionId) {
    const params = new URLSearchParams(window.location.search);
    params.delete('section_id');
    params.set('page', String(pageNum));

    if (sectionId) {
      params.set('section_id', sectionId);
    }

    const query = params.toString();
    return `${window.location.pathname}${query ? `?${query}` : ''}`;
  }

  function parsePageData (html, pageNum) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const parsedGrid = doc.getElementById('product-grid');

    if (!parsedGrid) {
      return {
        cards: {},
        items: [],
      };
    }

    const cards = {};
    const items = [];
    const elements = parsedGrid.querySelectorAll('[data-product-id]');

    elements.forEach((element) => {
      const productId = element.dataset.productId;
      if (!productId) return;

      cards[productId] = element.outerHTML;
      items.push({
        id: productId,
        available: element.dataset.available === 'true',
        page: pageNum,
      });
    });

    return {
      cards,
      items,
    };
  }

  async function fetchPageData (pageNum, sectionId, signal) {
    const url = buildSectionPageUrl(pageNum, sectionId);

    if (state.pageDataCache.has(url)) {
      return state.pageDataCache.get(url);
    }

    if (state.pageDataPromiseCache.has(url)) {
      return state.pageDataPromiseCache.get(url);
    }

    const requestPromise = fetch(url, { credentials: 'same-origin', signal })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to fetch collection section page ${pageNum}`);
        }

        return response.text();
      })
      .then((html) => {
        const data = parsePageData(html, pageNum);
        touchPageCache(url, data);
        return data;
      })
      .finally(() => {
        state.pageDataPromiseCache.delete(url);
      });

    state.pageDataPromiseCache.set(url, requestPromise);
    return requestPromise;
  }

  function readCurrentPageData (grid, pageNum) {
    const cards = {};
    const items = [];
    const elements = grid.querySelectorAll('[data-product-id]');

    elements.forEach((element) => {
      const productId = element.dataset.productId;
      if (!productId) return;

      cards[productId] = element.outerHTML;
      items.push({
        id: productId,
        available: element.dataset.available === 'true',
        page: pageNum,
      });
    });

    return {
      cards,
      items,
    };
  }

  async function fetchPagesWithLimit (pageNumbers, sectionId, signal) {
    const pageData = {};
    let cursor = 0;

    async function worker () {
      while (cursor < pageNumbers.length) {
        const pageIndex = cursor;
        cursor += 1;

        const pageNum = pageNumbers[pageIndex];
        const data = await fetchPageData(pageNum, sectionId, signal);
        pageData[pageNum] = data;
      }
    }

    const workers = [];
    const workerCount = Math.min(MAX_CONCURRENT_FETCHES, pageNumbers.length);
    for (let i = 0; i < workerCount; i += 1) {
      workers.push(worker());
    }

    await Promise.all(workers);
    return pageData;
  }

  function collectCardsFromCurrentGrid (grid) {
    const cards = {};
    const items = grid.querySelectorAll('[data-product-id]');

    items.forEach((item) => {
      const id = item.dataset.productId;
      if (!id) return;
      cards[id] = item.outerHTML;
    });

    return cards;
  }

  async function runReorder () {
    if (isReorderForceDisabled()) return;

    const context = getGridContext();
    if (!context) return;
    if (context.totalPages <= 1) return;
    if (hasBlockedQueryParams()) return;

    if (state.abortController) {
      state.abortController.abort();
    }

    state.abortController = new AbortController();
    const { signal } = state.abortController;

    const runId = state.runId + 1;
    state.runId = runId;

    const pageData = {};
    pageData[context.currentPage] = readCurrentPageData(context.grid, context.currentPage);

    const missingPages = [];
    for (let page = 1; page <= context.totalPages; page += 1) {
      if (page === context.currentPage) continue;
      missingPages.push(page);
    }

    const fetchedPageData = await fetchPagesWithLimit(missingPages, context.sectionId, signal);
    if (state.runId !== runId) return;

    Object.assign(pageData, fetchedPageData);

    const allItems = [];
    for (let page = 1; page <= context.totalPages; page += 1) {
      const data = pageData[page];
      if (!data) continue;
      allItems.push(...data.items);
    }

    if (!allItems.length) return;

    const availableIds = [];
    const unavailableIds = [];
    const idToPage = {};

    allItems.forEach((item) => {
      if (item.available) {
        availableIds.push(item.id);
      } else {
        unavailableIds.push(item.id);
      }

      if (!idToPage[item.id]) {
        idToPage[item.id] = item.page;
      }
    });

    const sortedIds = availableIds.concat(unavailableIds);
    const start = (context.currentPage - 1) * context.perPage;
    const targetIds = sortedIds.slice(start, start + context.perPage);

    const currentElements = context.grid.querySelectorAll('[data-product-id]');
    const currentIds = Array.from(currentElements).map((el) => el.dataset.productId);
    if (arraysEqual(currentIds, targetIds)) return;

    const cardsById = collectCardsFromCurrentGrid(context.grid);

    targetIds.forEach((targetId) => {
      if (cardsById[targetId]) return;

      const sourcePage = idToPage[targetId];
      if (!sourcePage || !pageData[sourcePage] || !pageData[sourcePage].cards[targetId]) return;

      cardsById[targetId] = pageData[sourcePage].cards[targetId];
    });

    const resolvedCount = targetIds.filter((id) => Boolean(cardsById[id])).length;
    if (resolvedCount !== targetIds.length) return;

    const nextHTML = targetIds.map((id) => cardsById[id] || '').join('');
    if (!nextHTML || state.runId !== runId) return;

    const validationDoc = new DOMParser().parseFromString(`<ul>${nextHTML}</ul>`, 'text/html');
    const parsedCount = validationDoc.querySelectorAll('[data-product-id]').length;
    if (parsedCount !== targetIds.length) return;

    const liveGrid = document.getElementById('product-grid');
    if (!liveGrid || !liveGrid.dataset.collectionHandle) return;

    liveGrid.innerHTML = nextHTML;

    const liveContainer = document.getElementById('ProductGridContainer');
    if (liveContainer) {
      FacetFiltersForm.normalizeProductGridVisibility(liveContainer);
    }

    if (liveContainer && typeof initializeScrollAnimationTrigger === 'function') {
      initializeScrollAnimationTrigger(liveContainer);
    }
  }

  function queueReorder () {
    if (state.queued) return;
    state.queued = true;

    Promise.resolve()
      .then(runReorder)
      .catch((error) => {
        if (error && error.name === 'AbortError') return;
        console.warn('Collection availability reorder skipped after error:', error);
      })
      .finally(() => {
        state.queued = false;
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', queueReorder);
  } else {
    queueReorder();
  }

  document.addEventListener('collection:product-grid:updated', queueReorder);
  window.addEventListener('pageshow', queueReorder);
  window.addEventListener('popstate', queueReorder);
})();
